import React, { useEffect, useState } from 'react';
import './Auth.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiEndpoint } from './config';
import soundEffects from './sounds';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [telescopeState, setTelescopeState] = useState('idle'); // idle, searching, locking, zooming
    const [emailEntered, setEmailEntered] = useState(false);
    const [passwordEntered, setPasswordEntered] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(soundEffects.isEnabled());
    const [showResendOption, setShowResendOption] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [cooldownTime, setCooldownTime] = useState(0);
    const [, setLastResendTime] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const message = params.get('message');
        const verified = params.get('verified');
        const emailChangeVerified = params.get('email_change_verified');

        if (!message) {
            return;
        }

        if (verified === '1' || emailChangeVerified === '1') {
            setResendSuccess(message);
            setError('');
        } else {
            setError(message);
            setResendSuccess('');
        }

        navigate('/login', { replace: true });
    }, [location.search, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Update telescope state based on field entry
        if (name === 'email' && value.trim()) {
            const wasEntered = emailEntered;
            setEmailEntered(true);
            setTelescopeState('searching');
            if (!wasEntered) {
                soundEffects.telescopeScan();
            }
        } else if (name === 'email' && !value.trim()) {
            setEmailEntered(false);
            setTelescopeState('idle');
        }

        if (name === 'password' && value.trim() && emailEntered) {
            const wasEntered = passwordEntered;
            setPasswordEntered(true);
            setTelescopeState('locking');
            if (!wasEntered) {
                soundEffects.lockOn();
            }
        } else if (name === 'password' && !value.trim()) {
            setPasswordEntered(false);
            if (emailEntered) {
                setTelescopeState('searching');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResendSuccess('');
        setLoading(true);
        setTelescopeState('zooming');
        soundEffects.whoosh();

        try {
            const response = await fetch(getApiEndpoint('api/auth/login'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password
                })
            });

            // Parse response - handle both success and error cases
            let data;
            try {
                data = await response.json();
            } catch (parseError) {
                console.error('Failed to parse response:', parseError);
                throw new Error('Invalid response from server');
            }

            // Check if login was successful
            if (response.ok && data.success) {
                // Store the JWT token
                localStorage.setItem('auth_token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                soundEffects.successChime();
                
                // Wait for zoom animation before navigating
                setTimeout(() => {
                    navigate('/');
                }, 1500);
            } else if (data.beta_not_approved) {
                // User is authenticated but not yet beta approved
                localStorage.setItem('auth_token', data.temp_token);
                localStorage.setItem('user', JSON.stringify(data.user));
                soundEffects.whoosh();
                setTelescopeState('locking');
                setTimeout(() => {
                    // If they've never requested (e.g. after rejection), send to re-request page
                    if (data.beta_access_requested) {
                        navigate('/beta-pending');
                    } else {
                        navigate('/beta-request');
                    }
                }, 800);
            } else {
                // Login failed - show appropriate error message
                const errorMsg = data.error || 'Invalid email or password. Please try again.';
                setError(errorMsg);
                
                console.log('Login failed:', {
                    status: response.status,
                    error: errorMsg,
                    email_not_verified: data.email_not_verified
                });
                
                // Check if error is about email verification using the flag from backend
                if (data.email_not_verified || errorMsg.toLowerCase().includes('verify') || errorMsg.toLowerCase().includes('verification')) {
                    setShowResendOption(true);
                    setResendEmail(formData.email); // Pre-fill with attempted email
                } else {
                    setShowResendOption(false);
                }
                
                setTelescopeState('searching');
                setLoading(false);
                soundEffects.errorBuzz();
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Connection error. Please check your internet and try again.');
            setShowResendOption(false);
            setTelescopeState('idle');
            setLoading(false);
            soundEffects.errorBuzz();
        }
    };

    const handleResendVerification = async (emailToUse = null) => {
        // Check cooldown
        if (cooldownTime > 0) {
            setError(`Please wait ${cooldownTime} seconds before resending.`);
            return;
        }

        const email = emailToUse || resendEmail || formData.email;
        
        if (!email || !email.includes('@')) {
            setError('Please enter a valid email address.');
            return;
        }

        setResending(true);
        setResendSuccess('');
        setError('');

        try {
            const response = await fetch(getApiEndpoint('api/auth/resend-verification'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (data.success) {
                setResendSuccess(data.message || 'Verification email sent successfully! Please check your inbox (and spam folder).');
                setShowResendOption(false);
                soundEffects.successChime();
                
                // Start 60-second cooldown
                startCooldown();
            } else {
                setError(data.error || 'Failed to resend verification email. Please try again.');
                soundEffects.errorBuzz();
            }
        } catch (err) {
            console.error('Resend verification error:', err);
            setError('Failed to resend verification email. Please try again.');
            soundEffects.errorBuzz();
        } finally {
            setResending(false);
        }
    };

    const startCooldown = () => {
        setLastResendTime(Date.now());
        setCooldownTime(60); // 60 seconds cooldown
        
        const interval = setInterval(() => {
            setCooldownTime(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const toggleSound = () => {
        const newState = soundEffects.toggle();
        setSoundEnabled(newState);
        if (newState) {
            soundEffects.beep();
        }
    };

    return (
        <div className="auth-container space-auth">
            <div className="stars-background"></div>

            {/* Sound toggle button */}
            <button className="sound-toggle" onClick={toggleSound} title={soundEnabled ? "Mute sounds" : "Enable sounds"}>
                {soundEnabled ? '🔊' : '🔇'}
            </button>

            <div className="login-content">
                {/* Telescope Visualization */}
                <div className="telescope-visualization">
                    <div className="telescope-container">
                        {/* Starfield with target star */}
                        <div className="starfield-view">
                            {/* Background stars */}
                            {[...Array(30)].map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-star"
                                    style={{
                                        left: `${Math.random() * 100}%`,
                                        top: `${Math.random() * 100}%`,
                                        animationDelay: `${Math.random() * 3}s`,
                                        opacity: Math.random() * 0.8 + 0.2
                                    }}
                                />
                            ))}

                            {/* Target star (user's star) */}
                            <div className={`target-star ${telescopeState}`}>
                                <svg viewBox="0 0 100 100" className="star-svg">
                                    <polygon
                                        points="50,15 61,40 88,40 67,58 73,85 50,70 27,85 33,58 12,40 39,40"
                                        fill="#FFD700"
                                        stroke="#FFA500"
                                        strokeWidth="2"
                                    />
                                    <circle cx="50" cy="50" r="8" fill="#FFF" opacity="0.8" />
                                </svg>
                                <div className="star-glow"></div>
                            </div>

                            {/* Crosshair overlay */}
                            <div className={`crosshair ${telescopeState === 'locking' || telescopeState === 'zooming' ? 'locked' : ''}`}>
                                <div className="crosshair-h"></div>
                                <div className="crosshair-v"></div>
                                <div className="crosshair-circle"></div>
                                {telescopeState === 'locking' && (
                                    <div className="lock-indicator">TARGET LOCKED</div>
                                )}
                            </div>

                            {/* Scanning beam */}
                            {telescopeState === 'searching' && (
                                <div className="scanning-beam"></div>
                            )}
                        </div>

                        {/* Telescope frame/border */}
                        <div className="telescope-frame">
                            <div className="frame-corner tl"></div>
                            <div className="frame-corner tr"></div>
                            <div className="frame-corner bl"></div>
                            <div className="frame-corner br"></div>
                        </div>

                        {/* Status display */}
                        <div className="telescope-status">
                            <div className="status-line">
                                <span className="status-label">STATUS:</span>
                                <span className="status-value">
                                    {telescopeState === 'idle' && 'STANDBY'}
                                    {telescopeState === 'searching' && 'SEARCHING...'}
                                    {telescopeState === 'locking' && 'LOCKING TARGET...'}
                                    {telescopeState === 'zooming' && 'APPROACHING...'}
                                </span>
                            </div>
                            <div className="status-line">
                                <span className="status-label">SIGNAL:</span>
                                <div className="signal-bars">
                                    <div className={`bar ${emailEntered ? 'active' : ''}`}></div>
                                    <div className={`bar ${passwordEntered ? 'active' : ''}`}></div>
                                    <div className={`bar ${telescopeState === 'locking' ? 'active' : ''}`}></div>
                                    <div className={`bar ${telescopeState === 'zooming' ? 'active' : ''}`}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Login Form */}
                <div className="auth-form space-form login-form">
                    <h2 className="space-title">
                        Sign In
                    </h2>
                    <p className="journey-subtitle">Welcome back</p>

                    {error && (
                        <div className="error-message asteroid-alert">
                            <svg className="alert-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                            </svg>
                            {error}
                        </div>
                    )}

                    {resendSuccess && (
                        <div className="resend-success-banner">
                            <span>✓</span>
                            {resendSuccess}
                        </div>
                    )}

                    {showResendOption && (
                        <div className="resend-verification-box">
                            <p className="resend-verification-text">
                                Didn't receive the verification email?
                            </p>
                            <button
                                type="button"
                                onClick={() => handleResendVerification(formData.email)}
                                disabled={resending || !formData.email || cooldownTime > 0}
                                className={`resend-verification-btn ${cooldownTime > 0 ? 'cooldown' : ''}`}
                            >
                                {cooldownTime > 0
                                    ? `⏱ Wait ${cooldownTime}s`
                                    : resending
                                        ? 'Sending…'
                                        : 'Resend Verification Email'}
                            </button>
                            {cooldownTime > 0 && (
                                <p className="resend-cooldown-note">
                                    Please wait before sending again
                                </p>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group space-form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="you@example.com"
                                className={emailEntered ? 'field-completed' : ''}
                                required
                            />
                            {emailEntered && <span className="field-check">✓</span>}
                        </div>

                        <div className="form-group space-form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••••"
                                className={passwordEntered ? 'field-completed' : ''}
                                required
                            />
                            {passwordEntered && <span className="field-check">✓</span>}
                        </div>

                        <button
                            type="submit"
                            className={`auth-button telescope-button ${
                                emailEntered && passwordEntered ? 'ready-to-lock' : ''
                            } ${loading ? 'zooming' : ''}`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="button-icon pulse"></span>
                                    Signing you in...
                                </>
                            ) : emailEntered && passwordEntered ? (
                                <>
                                    <span className="button-icon pulse"></span>
                                    Sign In
                                </>
                            ) : (
                                <>
                                    <span className="button-icon"></span>
                                    Sign In
                                </>
                            )}
                        </button>
                    </form>

                    <div className="auth-links space-links">
                        <p>Don't have an account? <span onClick={() => navigate('/signup')} className="link">Create one</span></p>
                        <p>
                            Want to see what Launchway offers?{' '}
                            <span onClick={() => navigate('/features-of-launchway')} className="link">
                                View features
                            </span>
                        </p>
                        <p><span onClick={() => navigate('/')} className="link">← Back to home</span></p>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Login;

